import {
  evolve,
  groupBy,
  has,
  isEmpty,
  isNil,
  mergeRight,
  pluck,
  prop,
  propOr,
  reject,
} from '../../utils/fp';
import * as rules from '../rules';
import { isPerfectMatch } from '../../utils';
import { computeLevelSelections } from '../sdmx/selection';

// R.match returns [] (not null) on no match
const matchConstraintId = str => str.match(/#(.*?)#/g) || [];

export const searchConstraintsToVisConstraints = constraints => {
  const list = Object.values(constraints).map(item =>
    evolve(
      {
        constraintId: constraintId => {
          const matched = matchConstraintId(constraintId);
          // IE doesn't get /(?<=#)(.*?)(?=#)/g
          return isEmpty(matched) ? constraintId : matched.at(-1).replace(/#/g, '');
        },
      },
      item,
    ),
  );
  return groupBy(item => item.facetId, list);
};

const hlConstraintsToVisConstraints = pairs =>
  pairs.reduce((acc, [field, highlights]) => {
    const ids = highlights.reduce((_acc, { label, valueId }) => {
      if (!valueId) return _acc;
      return isPerfectMatch(label) ? [..._acc, valueId] : _acc;
    }, []);
    return isEmpty(ids) ? acc : { ...acc, [field]: ids };
  }, {});

const rejectNotDisplayedValueIds = values =>
  values.reduce((acc, value) => (value.display === false ? acc : [...acc, value.id]), []);

export const searchConstraintsToVisSelection = (dimensions, constraints, highlights) => {
  const visContraints = searchConstraintsToVisConstraints(constraints);
  const hlConstraints = hlConstraintsToVisConstraints(highlights);
  return dimensions.reduce((selection, dimension) => {
    // empty dimension is possible, don't remember the usecase (cf dimitri)
    if (isEmpty(dimension)) return selection;
    // from user or search not displayed dimension cannot be selected or unselected
    // see ticket https://gitlab.com/sis-cc/.stat-suite/dotstatsuite-data-explorer/-/issues/672
    if (dimension.display === false) return selection;

    const dimensionLabel = dimension.name;
    const id = dimension.id;
    const valueIds = new Set(rejectNotDisplayedValueIds(propOr([], 'values', dimension)));
    if (has(dimensionLabel, visContraints)) {
      const dimensionSelection = pluck('constraintId', visContraints[dimensionLabel]).filter(id =>
        valueIds.has(id),
      );

      return { ...selection, [id]: dimensionSelection };
    }
    if (has(dimensionLabel, hlConstraints)) {
      const dimensionSelection = hlConstraints[dimensionLabel].filter(id => valueIds.has(id));
      return isEmpty(dimensionSelection) ? selection : { ...selection, [id]: dimensionSelection };
    }

    return selection;
  }, {});
};

export const getDefaultSelection = (
  dimensions,
  structureSelection,
  constraints,
  highlightedConstraints,
  hierarchies,
  actualContentConstaint,
) => {
  const searchSelection = mergeRight(
    structureSelection,
    searchConstraintsToVisSelection(dimensions, constraints, highlightedConstraints),
  );

  const reduced = dimensions.reduce((acc, dimension) => {
    const id = dimension.id;
    let selectedValues = searchSelection[id];
    // prop, not a direct index: actualContentConstaint and hierarchies below are
    // optional (a dataflow without hierarchies passes undefined), and the R.prop
    // these replaced returned undefined instead of throwing.
    const dimContentConstraint = prop(id, actualContentConstaint);
    const isContentConstraintValid = id =>
      isNil(dimContentConstraint) || isEmpty(dimContentConstraint) || dimContentConstraint.has(id);

    if (isNil(id) || isNil(selectedValues)) {
      if (rules.isFrequencyDimension(dimension)) {
        const selectableValues = propOr([], 'values', dimension).filter(v =>
          isContentConstraintValid(v.id),
        );
        if (selectableValues.length === 1) {
          return { ...acc, [id]: [selectableValues[0].id] };
        }
      }
      return acc;
    }
    // R.match returns [] (truthy) on no match, so this branch always ran; preserved
    const hasLevelSelection = selectedValues.join(',').match(/LEVEL([\d]+)/) || [];
    if (hasLevelSelection) {
      selectedValues = computeLevelSelections(
        selectedValues,
        dimension,
        prop(dimension.id, hierarchies),
      );
    }
    const valueIds = new Set(pluck('id', propOr([], 'values', dimension)));
    return {
      ...acc,
      [id]: selectedValues.filter(id => valueIds.has(id) && isContentConstraintValid(id)),
    };
  }, {});

  return reject(isEmpty, reduced);
};
